import React from 'react';
import { Info } from 'lucide-react';

interface SeatsIndicatorProps {
  occupiedSeats: number;
  totalSeats: number;
  warningThreshold?: number;
  onClick?: () => void;
}

export const SeatsIndicator: React.FC<SeatsIndicatorProps> = ({
  occupiedSeats,
  totalSeats,
  warningThreshold = 2,
  onClick,
}) => {
  const availableSeats = totalSeats - occupiedSeats;

  if (totalSeats <= 0) {
    return null;
  }

  const isClickable = !!onClick;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      aria-label={
        isClickable
          ? `Seat usage details, ${occupiedSeats} of ${totalSeats} seats used`
          : undefined
      }
      className={`flex text-center justify-center text-nowrap gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 ${
        isClickable
          ? 'cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-400'
          : ''
      }`}>
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-green-500"></div>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {occupiedSeats}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">/</span>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {totalSeats}
        </span>
      </div>
      <span className="text-xs text-center text-gray-500 dark:text-gray-400">
        seats
      </span>
      {availableSeats <= warningThreshold && availableSeats > 0 && (
        <span className="text-xs text-orange-600 dark:text-orange-400 ml-1">
          ({availableSeats} left)
        </span>
      )}
      {availableSeats <= 0 && (
        <span className="text-xs text-red-600 dark:text-red-400 ml-1">
          (full)
        </span>
      )}
      {isClickable && (
        <Info
          className="w-4 h-4 text-gray-400 dark:text-gray-500 ml-0.5 self-center"
          aria-hidden="true"
        />
      )}
    </div>
  );
};
